// client/s    rc/features/quote-builder/QuoteSectionCard.js
import React from 'react';
import 'react-quill-new/dist/quill.snow.css';

// Note: We are using simple SVG icons for now, can replace with a library later if desired.

function QuoteSectionCard({ section, onEdit, onRemove, onDuplicate, calculateSectionTotalsForDisplay }) {
  // Calculate section total for display
  const sectionTotals = typeof calculateSectionTotalsForDisplay === 'function'
    ? calculateSectionTotalsForDisplay(section)
    : { subtotal: 0 };

  return (
    <div className="border border-gray-200 rounded-lg p-5 shadow-sm bg-white">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-3">
        <div className="flex-1">
          <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-1">{section.name}</h3>
          {section.description && (
            <div
              className="text-sm sm:text-base text-gray-600 prose prose-sm max-w-none quote-section-description mb-2"
              dangerouslySetInnerHTML={{ __html: formatDescription(section.description) }}
            />
          )}
        </div>
        <div className="flex space-x-2 sm:space-x-3 justify-end sm:justify-start">
          {/* Edit Button */}
          <button
            type="button"
            onClick={() => onEdit(section.id)}
            className="p-3 sm:p-2 bg-primary-100 text-primary-700 rounded-full hover:bg-primary-200 transition duration-150 ease-in-out min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
            aria-label={`Edit ${section.name}`}
            title="Edit Section Details"
          >
            <svg className="w-6 h-6 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
          </button>
          {/* Duplicate Button */}
          <button
            type="button"
            onClick={() => onDuplicate(section.id)}
            className="p-3 sm:p-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition duration-150 ease-in-out min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
            aria-label={`Duplicate ${section.name}`}
            title="Duplicate Section"
          >
            <svg className="w-6 h-6 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v4a2 2 0 002 2h4m-4 0h4m-4 0v4m0 0h6m-6 0h6M5 12h-.01M17 12h-.01M12 5h.01M12 17h.01M12 7V5m0 0H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
          </button>
          {/* Remove Button */}
          <button
            type="button"
            onClick={() => onRemove(section.id)}
            className="p-3 sm:p-2 bg-red-100 text-red-700 rounded-full hover:bg-red-200 transition duration-150 ease-in-out min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center"
            aria-label={`Remove ${section.name}`}
            title="Remove Section"
          >
            <svg className="w-6 h-6 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
          </button>
        </div>
      </div>
      <div className="flex justify-end items-center border-t border-gray-100 pt-3 mt-3">
        <span className="text-lg font-semibold text-gray-800"></span>
        <span className="text-2xl sm:text-xl font-extrabold text-primary-700 ml-2">
          ${sectionTotals.subtotal.toFixed(2)}
        </span>
      </div>
    </div>
  );
}

const formatDescription = (description) => {
  if (!description) return '';

  // Apply styling to unordered lists - let CSS handle nested levels
  let formatted = description.replace(/<ul[^>]*>/g, '<ul>');

  // Apply styling to ordered lists - let CSS handle nested levels
  formatted = formatted.replace(/<ol[^>]*>/g, '<ol>');

  // Remove inline styles from list items that might override our CSS
  formatted = formatted.replace(/<li[^>]*style="[^"]*"[^>]*>/g, (match) => {
    return match.replace(/style="[^"]*"/g, '');
  });

  // Clean up any Quill-specific classes that might interfere
  formatted = formatted.replace(/class="ql-indent-\d+"/g, '');

  return formatted;
};

export default QuoteSectionCard;
